/**
 * Electronic-signature content binding and manifestation (PROJECT_SPEC.md §5.6; 21 CFR 11.50, 11.70;
 * draft Annex 11 §13.6/13.8). Shared server↔client so the client can show the exact hash preview the
 * server will re-verify. sign H(canonical(subject_state) ‖ meaning ‖ signer_id ‖ server_time ‖ content_hash_at_time).
 */
import { z } from "zod";
import { canonicalBytes, concatBytes, type Json, sha256Hex } from "../canonical/jcs.js";

// @__PURE__ lets the web bundle drop zod when only the helpers below are imported
export const SignatureMeaning = /* @__PURE__ */ z.enum([
  "PERFORMED",
  "VERIFIED",
  "REVIEWED",
  "APPROVED",
  "REJECTED",
  "WITNESSED",
  "AUTHORED",
  "CHECKED",
  "CERTIFIED_QP",
]);
export type SignatureMeaning = z.infer<typeof SignatureMeaning>;

/** Meanings that require an earlier signature of another meaning on the same subject (ESIG-020/022). */
export const SEQUENTIAL_PREREQUISITE: Partial<Record<SignatureMeaning, SignatureMeaning[]>> = {
  VERIFIED: ["PERFORMED"],
  WITNESSED: ["PERFORMED"],
  CHECKED: ["AUTHORED"],
  APPROVED: ["CHECKED", "REVIEWED", "VERIFIED", "PERFORMED"], // any one present suffices — the subject type decides which (see resolver)
  CERTIFIED_QP: ["APPROVED"],
};

/** Meanings where the signer must differ from the signer of the prerequisite (ESIG-021, N9). */
export const MUST_DIFFER_FROM_PREREQUISITE: ReadonlySet<SignatureMeaning> = new Set([
  "VERIFIED",
  "WITNESSED",
  "CHECKED",
  "APPROVED",
  "CERTIFIED_QP",
]);

/** Subject state hashed at signing time: the caller passes the exact canonical subject snapshot. */
export async function subjectStateHash(subjectState: Json): Promise<string> {
  return sha256Hex(canonicalBytes(subjectState));
}

export interface BindingInput {
  subjectStateHashHex: string;
  meaning: SignatureMeaning;
  signerUserId: string;
  serverTimeIso: string;
  contentHashAtTimeHex: string; // batch chain head at signing time
}

/** The bytes that are signed (§5.6). Any later change to the subject changes subjectStateHash → signature no longer binds (ESIG-024). */
export async function signedPayloadHash(b: BindingInput): Promise<string> {
  const enc = new TextEncoder();
  return sha256Hex(
    concatBytes(
      hexToBytes(b.subjectStateHashHex),
      enc.encode(`|${b.meaning}|${b.signerUserId}|${b.serverTimeIso}|`),
      hexToBytes(b.contentHashAtTimeHex),
    ),
  );
}

export interface Manifestation {
  full_name: string;
  username: string;
  role: string | null;
  meaning: SignatureMeaning;
  signed_at: string; // ISO UTC
  tz: string; // IANA zone used for rendering
  batch_number: string;
  subject_ref: string;
}

/** One renderer for screen, PDF and export (11.50(b), ESIG-014). Format: "Signed VERIFIED · Carol QA (carol.qa, QA reviewer) · 2026-09-06 14:32:07 EET". */
export function manifestationText(m: Manifestation): string {
  const when = formatInZone(m.signed_at, m.tz);
  const role = m.role ? `, ${m.role}` : "";
  return `Signed ${m.meaning} · ${m.full_name} (${m.username}${role}) · ${when}`;
}

/** yyyy-MM-dd HH:mm:ss ZONE, zone label always displayed (TIME-007, AT-025). */
export function formatInZone(iso: string, tz: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  }).formatToParts(d);
  const p = (t: string): string => parts.find((x) => x.type === t)?.value ?? "";
  return `${p("year")}-${p("month")}-${p("day")} ${p("hour")}:${p("minute")}:${p("second")} ${p("timeZoneName")}`;
}

function hexToBytes(hex: string): Uint8Array {
  if (!/^[0-9a-f]{64}$/i.test(hex)) throw new Error("expected 32-byte hex digest");
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i++) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}
